import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../../components/utils";
import {
  deleteTransactionDocuments,
  getTransactionDocuments,
  uploadTransactionDocuments,
} from "../../../../api/TransactionApisV2";
import { UploadedFile } from "../../../../types/Transaction";
import convertToBase64 from "../../../../utils/convertToBase64";

const DocumentsUploaded = ({
  employeeId,
  transactionId,
  onClose,
}: {
  employeeId: string;
  transactionId: string;
  onClose: () => void;
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (employeeId && transactionId) {
      getTransactionDocuments({
        employeeId,
        transactionId,
      })
        .then((res) => {
          setUploadedFiles(res.data);
        })
        .catch((err) => {
          toast.error(
            `Failed to fetch documents: ${
              err.response?.data?.errorRaw || err.message
            }`
          );
        });
    }
  }, [employeeId, transactionId]);

  const createDownloadUrl = (base64String: string) => {
    let base64Content = base64String;
    if (base64String.startsWith("data:application/octet-stream;base64,")) {
      base64Content = base64String.split(",")[1];
    }
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/octet-stream" });
    return URL.createObjectURL(blob);
  };

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      const convertedFiles: UploadedFile[] = await Promise.all(
        newFiles.map(async (file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          base64: (await convertToBase64(file)) as string,
        }))
      );
      setPendingFiles((prev) => [...prev, ...convertedFiles]);
    }
  }

  function handleRemoveFile(index: number, fileType: "uploaded" | "pending") {
    if (fileType === "uploaded") {
      const file = uploadedFiles[index];
      setDeletedFiles((prev) => [...prev, file]);
      setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    }
  }

  async function handleSave() {
    if (!transactionId) {
      toast.error("Please select a transaction before uploading documents.");
      return;
    }

    setLoading(true);
    // Upload pending files
    let uploadPromise = Promise.resolve();
    if (pendingFiles.length > 0) {
      uploadPromise = uploadTransactionDocuments({
        employeeId,
        transactionId,
        files: pendingFiles,
      })
        .then(() => {
          setUploadedFiles((prev) => [...prev, ...pendingFiles]);
          setPendingFiles([]);
          toast.success("Documents uploaded successfully!");
        })
        .catch((err) => {
          toast.error(
            `Failed to upload documents: ${
              err.response?.data?.errorRaw || err.message
            }`
          );
        });
    }
    // Delete marked files
    let deletePromise = Promise.resolve();
    if (deletedFiles.length > 0) {
      const deleteRequests = deletedFiles
        .filter((file) => !!file.id)
        .map((file) => deleteTransactionDocuments(file.id || ""));
      if (deleteRequests.length > 0) {
        deletePromise = Promise.all(deleteRequests)
          .then(() => {
            setDeletedFiles([]);
            toast.success("Documents deleted successfully!");
          })
          .catch((err) => {
            toast.error(
              `Failed to delete documents: ${
                err.response?.data?.errorRaw || err.message
              }`
            );
          });
      }
    }
    await Promise.all([uploadPromise, deletePromise]);
    setLoading(false);
    onClose();
  }

  return (
    <VStack className="min-w-[600px] mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <HStack className="items-center justify-between p-6 border-b">
        <span className="text-lg font-semibold">Documents</span>
        <button
          className="text-gray-400 transition hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon icon="mdi:close" width={24} height={24} />
        </button>
      </HStack>

      {/* Upload Button */}
      <HStack className="justify-end gap-2 px-6 pt-6">
        <ButtonPrimary1 onClick={() => fileInputRef.current?.click()}>
          {uploadedFiles.length + pendingFiles.length === 0
            ? "Upload Document"
            : "Add Another Document"}
        </ButtonPrimary1>
        <input
          ref={fileInputRef}
          id="docFileInput"
          type="file"
          multiple
          className="hidden"
          onChange={handleFileUpload}
        />
      </HStack>

      {/* File List */}
      <VStack className="gap-4 px-6 py-6">
        {uploadedFiles.length + pendingFiles.length + deletedFiles.length ===
        0 ? (
          <div className="py-6 text-sm text-center text-gray-400">
            No documents uploaded yet.
          </div>
        ) : (
          <>
            {/* Uploaded files */}
            {uploadedFiles.map((file, index) => (
              <HStack
                key={`uploaded-${file.id || index}`}
                className="items-center justify-between px-6 py-4 transition border-2 rounded-lg bg-gray-50 hover:bg-gray-100"
              >
                <HStack className="items-center gap-3">
                  <Icon
                    icon={
                      file.type.includes("pdf")
                        ? "vscode-icons:file-type-pdf2"
                        : file.type.includes("spreadsheet") ||
                          file.name.endsWith(".xlsx") ||
                          file.name.endsWith(".xls")
                        ? "vscode-icons:file-type-excel"
                        : file.type.includes("word") ||
                          file.name.endsWith(".docx") ||
                          file.name.endsWith(".doc")
                        ? "vscode-icons:file-type-word"
                        : file.type.includes("image")
                        ? "vscode-icons:file-type-image"
                        : "mdi:file-document"
                    }
                    height={30}
                    className="text-blue-400"
                  />
                  <p className="text-base font-medium">{file.name}</p>
                </HStack>
                <HStack className="items-center gap-4">
                  <a
                    href={createDownloadUrl(file.base64)}
                    download={file.name}
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon="material-symbols:download-sharp" height={24} />
                  </a>
                  <button
                    onClick={() => handleRemoveFile(index, "uploaded")}
                    className=""
                    aria-label="Remove"
                    disabled={loading}
                  >
                    <Icon icon="mdi:delete" width={20} height={20} />
                  </button>
                </HStack>
              </HStack>
            ))}
            {/* Pending files */}
            {pendingFiles.map((file, index) => (
              <HStack
                key={`pending-${index}`}
                className="items-center justify-between px-6 py-4 transition border-2 rounded-lg bg-yellow-50 hover:bg-yellow-100"
              >
                <HStack className="items-center gap-3">
                  <Icon
                    icon={
                      file.type.includes("pdf")
                        ? "vscode-icons:file-type-pdf2"
                        : file.type.includes("spreadsheet") ||
                          file.name.endsWith(".xlsx") ||
                          file.name.endsWith(".xls")
                        ? "vscode-icons:file-type-excel"
                        : file.type.includes("word") ||
                          file.name.endsWith(".docx") ||
                          file.name.endsWith(".doc")
                        ? "vscode-icons:file-type-word"
                        : file.type.includes("image")
                        ? "vscode-icons:file-type-image"
                        : "mdi:file-document"
                    }
                    height={30}
                    className="text-blue-400"
                  />
                  <p className="text-base font-medium">{file.name}</p>
                  <span className="ml-2 px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                    Will be added
                  </span>
                </HStack>
                <HStack className="items-center gap-4">
                  <a
                    href={createDownloadUrl(file.base64)}
                    download={file.name}
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon="material-symbols:download-sharp" height={24} />
                  </a>
                  <button
                    onClick={() => handleRemoveFile(index, "pending")}
                    className=""
                    aria-label="Remove"
                    disabled={loading}
                  >
                    <Icon icon="mdi:delete" width={20} height={20} />
                  </button>
                </HStack>
              </HStack>
            ))}
            {/* Deleted files */}
            {deletedFiles.map((file, index) => (
              <HStack
                key={`deleted-${file.id || index}`}
                className="items-center justify-between px-6 py-4 transition border-2 rounded-lg bg-red-50 hover:bg-red-100 opacity-70"
              >
                <HStack className="items-center gap-3">
                  <Icon
                    icon={
                      file.type.includes("pdf")
                        ? "vscode-icons:file-type-pdf2"
                        : file.type.includes("spreadsheet") ||
                          file.name.endsWith(".xlsx") ||
                          file.name.endsWith(".xls")
                        ? "vscode-icons:file-type-excel"
                        : file.type.includes("word") ||
                          file.name.endsWith(".docx") ||
                          file.name.endsWith(".doc")
                        ? "vscode-icons:file-type-word"
                        : file.type.includes("image")
                        ? "vscode-icons:file-type-image"
                        : "mdi:file-document"
                    }
                    height={30}
                    className="text-blue-400"
                  />
                  <p className="text-base font-medium line-through">
                    {file.name}
                  </p>
                  <span className="ml-2 px-2 py-1 text-xs rounded bg-red-100 text-red-700">
                    Will be deleted
                  </span>
                </HStack>
              </HStack>
            ))}
          </>
        )}
      </VStack>

      {/* Save Button */}
      {(pendingFiles.length > 0 || deletedFiles.length > 0) && (
        <HStack className="justify-end pt-4 pb-8 pr-8">
          <ButtonPrimary
            disabled={loading}
            onClick={handleSave}
            loading={loading}
          >
            Save
          </ButtonPrimary>
        </HStack>
      )}
    </VStack>
  );
};

export default DocumentsUploaded;
